/**
 *
 * @module component/master
 * @author shojib [mahmud.hossain@mlb.com]
 * @description entry point for all component specific functionality
 *
 */

/**
 *
 * @interface ComponentInterface
 * @method {Function<*(Object)>} init - accepts a data object that has been extended. Return type optional.
 * @method {Function<void(ViewModel)>} constructor - accepts a ViewModel instance.
 *
 */

/**
 *
 * @class Component
 * @implements ComponentInterface
 *
 * @desc There are a few signatures to adhere to within this file.
 *
 * 1. Export a function with signature Component(ViewModel), i.e. accept a ViewModel instance
 *    and return an instance of your component.
 *
 * 2. As defined in the Component interface, define at least a constructor accepting a ViewModel parent
 *    and an init function that accepts a data object which has already passed through the extender.
 *
 * For external communication, events can be emitted via the parent ViewModel.
 * The provided methods other than those noted above are examples and can be removed.
 *
 */
class Component {

    /**
     *
     * @param {ViewModel} parent
     * @desc Your component constructor is called after the template has been
     * attached to the document by ViewModel.bindComponent(), which in turn happens
     * after a data event.
     *
     * 1. Model uses XHR to fetch data.
     * 2. ViewModel uses extender on the data.
     * 3. ViewModel generates the template with extended data.
     * 4. ViewModel invokes Component constructor with itself as parent and init(data). <- you are here.
     *
     */
    constructor(parent) {

        const { opts, element, container, dataCache } = parent;

        this.dataCache = dataCache;
        this.element = element || container;
        this.parent = parent;
        this.opts = opts;

    }

    /**
     *
     * @param {Object} data
     * @returns {Component} this
     * @desc instantiates component with a reference to the parent viewModel, properties on
     * the parent reference should never be modified in any way.
     *
     */
    init(data = {}) {

        this.dataCache = data;
        this.onRender();
        return this;

    }

    /* Methods below are optional, add/remove/modify as needed. */

    /**
     *
     * @returns {Component} this
     * @desc attaches component functionality.
     *
     */
    onRender() {

        this.addClasses().addEvents();

        return this;

    }

    /**
     *
     * @returns {Component}
     * @desc add component events, event listeners should be delegated from primary element
     *
     */
    addEvents() {

        const { element } = this;

        element.addEventListener('click', Component.clickHandler);

        return this;

    }

    /**
     *
     * @returns {Component} this
     * @desc toggles modifiers based on device features
     *
     */
    addClasses() {

        const { element } = this;

        return this;

    }

    /**
     *
     * @returns {Component} this
     * @desc detaches component functionality, all events must be cleaned up
     *
     */
    destroy() {

        const { element } = this;

        element.removeEventListener('click', Component.clickHandler);

        return this;

    }

    /**
     *
     * @param e Event
     * @desc Sample component click event handler
     *
     */
    static clickHandler(e) {

    }

}

/**
 *
 * @function componentFactory
 * @param {ViewModel} parent
 * @returns {Component}
 *
 */
export default function componentFactory(parent) {

    return new Component(parent);

}
